import { ReactNode } from "react";
import { useParams, useLocation, useNavigate, Outlet } from "react-router-dom";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { JobListPortal } from "../../components/job-list/job-list-portal.component";
import { useConnectStore } from "../../store/Connect";
import {
	Building2,
	Briefcase,
	FileText,
	MessageCircle,
	Plus,
} from "lucide-react";
import { VendorJobList } from "./vendor-job-list.component";
import { useState, useEffect } from "react";
import { cn } from "../../lib/utils/cn";

export interface VendorTab {
	key: string;
	label: string;
	icon: ReactNode;
	path?: string;
}

const DEFAULT_TABS: VendorTab[] = [
	{
		key: "auftraege",
		label: "Aufträge",
		icon: <Briefcase size={16} className="mr-1" />,
	},
	{
		key: "auftragsanfrage",
		label: "Auftragsanfrage",
		icon: <Plus size={16} className="mr-1" />,
		path: "job-request",
	},
	{
		key: "rechnungen",
		label: "Rechnungen",
		icon: <FileText size={16} className="mr-1" />,
		path: "invoices",
	},
	{
		key: "chat",
		label: "Chat",
		icon: <MessageCircle size={16} className="mr-1" />,
		path: "chat",
	},
];

interface VendorDetailPageProps {
	customerOrgId: string;
	tabs?: VendorTab[];
	onOpenSearch?: () => void;
}

export const VendorDetailPage: React.FC<VendorDetailPageProps> = ({
	customerOrgId,
	tabs = DEFAULT_TABS,
	onOpenSearch,
}) => {
	const { vendorId } = useParams<{ vendorId: string }>();
	const location = useLocation();
	const navigate = useNavigate();
	const brandsByOrgId = useConnectStore((state) => state.brandsByOrgId);
	const [isJobListView, setIsJobListView] = useState(true);

	const vendorName = vendorId
		? brandsByOrgId[vendorId]?.name ?? "Unbekannt"
		: "Unbekannt";
	const vendorAdminEmail = vendorId
		? brandsByOrgId[vendorId]?.admin_email ?? null
		: null;
	const vendorTitle = vendorAdminEmail
		? `${vendorName} (${vendorAdminEmail})`
		: vendorName;

	const isOnJobRoute = () => {
		const path = location.pathname;
		const baseVendorPath = `/vendor/${vendorId}`;
		return (
			path === baseVendorPath ||
			path.includes(`${baseVendorPath}/job/`) ||
			path.endsWith(`${baseVendorPath}/verrechnet`) ||
			path.endsWith(`${baseVendorPath}/archiv`)
		);
	};

	const isTabActive = (tab: VendorTab) => {
		if (!tab.path) return isOnJobRoute();
		return location.pathname.includes(`/vendor/${vendorId}/${tab.path}`);
	};

	const handleTabClick = (tab: VendorTab) => {
		if (!tab.path) {
			if (!isOnJobRoute()) {
				navigate(`/vendor/${vendorId}`);
			}
			setIsJobListView(!isJobListView);
		} else {
			navigate(`/vendor/${vendorId}/${tab.path}`);
		}
	};

	const shouldShowJobList = isOnJobRoute() && isJobListView;

	useEffect(() => {
		if (isOnJobRoute()) {
			setIsJobListView(true);
		}
	}, [location.pathname]);

	return (
		<>
			<HeaderPortal>
				<div className="flex items-center w-full gap-2">
					<PageTitle
						title={[{ name: vendorTitle }]}
						icon={<Building2 size={16} className="text-gray-700" />}
					/>
					<div className="flex items-center gap-1">
						{tabs.map((tab) => (
							<button
								key={tab.key}
								onClick={() => handleTabClick(tab)}
								className={cn(
									"flex items-center gap-1 px-3 py-1 text-sm rounded-md transition-colors",
									isTabActive(tab)
										? "bg-stone-200 text-gray-800 font-medium"
										: "text-gray-700 hover:bg-stone-100"
								)}
								aria-label={tab.label}
								data-testid={`vendor-tab-${tab.key}`}
								{...(tab.key === "auftraege"
									? {
											"aria-expanded": shouldShowJobList,
											"aria-controls": "job-list-content",
										}
									: {})}
							>
								{tab.icon}
								<span>{tab.label}</span>
							</button>
						))}
					</div>
				</div>
			</HeaderPortal>

			<JobListPortal
				isOpen={shouldShowJobList}
				onToggle={setIsJobListView}
			>
				<div className="h-full py-1 pl-1" id="job-list-content">
					<div className="relative bg-white rounded-xl shadow-sm overflow-hidden h-full">
						<div className="px-1 py-2 h-full">
							{vendorId && (
								<VendorJobList
									vendorOrgId={vendorId}
									customerOrgId={customerOrgId}
									onOpenSearch={onOpenSearch}
								/>
							)}
						</div>
					</div>
				</div>
			</JobListPortal>

			<Outlet />
		</>
	);
};
